import type { Entity } from '../entities/entity';
import type { EventBus, EntityType } from './eventBus';

export type EntityLifecycleHandler<T extends Entity = Entity> = (entity: T) => void;

export class EntityRegistry {
  private entities = new Map<EntityType, Map<string, Entity>>();
  private addHandlers = new Map<EntityType, Set<EntityLifecycleHandler>>();
  private removeHandlers = new Map<EntityType, Set<EntityLifecycleHandler>>();
  private eventBus: EventBus | null = null;

  constructor(eventBus?: EventBus) {
    this.eventBus = eventBus ?? null;
  }

  setEventBus(eventBus: EventBus): void {
    this.eventBus = eventBus;
  }

  add<T extends Entity>(type: EntityType, entity: T): void {
    if (!this.entities.has(type)) {
      this.entities.set(type, new Map());
    }
    this.entities.get(type)!.set(entity.id, entity);

    const handlers = this.addHandlers.get(type);
    if (handlers) {
      for (const handler of handlers) {
        try {
          handler(entity);
        } catch (error) {
          console.error(`[EntityRegistry] Error in add handler for '${type}':`, error);
        }
      }
    }

    if (this.eventBus) {
      this.eventBus.emit('entity:spawned', {
        type,
        id: entity.id,
        position: { x: entity.position.x, y: entity.position.y },
      });
    }
  }

  get<T extends Entity>(type: EntityType, id: string): T | undefined {
    return this.entities.get(type)?.get(id) as T | undefined;
  }

  remove(type: EntityType, id: string): boolean {
    const typeMap = this.entities.get(type);
    if (!typeMap) return false;

    const entity = typeMap.get(id);
    if (!entity) return false;

    typeMap.delete(id);

    const handlers = this.removeHandlers.get(type);
    if (handlers) {
      for (const handler of handlers) {
        try {
          handler(entity);
        } catch (error) {
          console.error(`[EntityRegistry] Error in remove handler for '${type}':`, error);
        }
      }
    }

    if (this.eventBus) {
      this.eventBus.emit('entity:destroyed', {
        type,
        id: entity.id,
        position: { x: entity.position.x, y: entity.position.y },
        reason: 'removed',
      });
    }

    return true;
  }

  getAll<T extends Entity>(type: EntityType): T[] {
    const typeMap = this.entities.get(type);
    if (!typeMap) return [];
    return Array.from(typeMap.values()) as T[];
  }

  getActive<T extends Entity>(type: EntityType): T[] {
    return this.getAll<T>(type).filter((e) => e.active);
  }

  find<T extends Entity>(type: EntityType, predicate: (entity: T) => boolean): T | undefined {
    const typeMap = this.entities.get(type);
    if (!typeMap) return undefined;
    for (const entity of typeMap.values()) {
      if (predicate(entity as T)) {
        return entity as T;
      }
    }
    return undefined;
  }

  filter<T extends Entity>(type: EntityType, predicate: (entity: T) => boolean): T[] {
    return this.getAll<T>(type).filter(predicate);
  }

  has(type: EntityType, id: string): boolean {
    return this.entities.get(type)?.has(id) ?? false;
  }

  count(type: EntityType): number {
    return this.entities.get(type)?.size ?? 0;
  }

  countActive(type: EntityType): number {
    return this.getActive(type).length;
  }

  countAll(): number {
    let total = 0;
    for (const typeMap of this.entities.values()) {
      total += typeMap.size;
    }
    return total;
  }

  clear(type: EntityType): void {
    const typeMap = this.entities.get(type);
    if (!typeMap) return;

    const handlers = this.removeHandlers.get(type);
    if (handlers) {
      for (const entity of typeMap.values()) {
        for (const handler of handlers) {
          try {
            handler(entity);
          } catch (error) {
            console.error(`[EntityRegistry] Error in remove handler for '${type}':`, error);
          }
        }
      }
    }

    typeMap.clear();
  }

  clearAll(): void {
    for (const type of this.entities.keys()) {
      this.clear(type);
    }
  }

  removeInactive(type: EntityType): number {
    const typeMap = this.entities.get(type);
    if (!typeMap) return 0;

    const toRemove: string[] = [];
    for (const [id, entity] of typeMap) {
      if (!entity.active) {
        toRemove.push(id);
      }
    }

    for (const id of toRemove) {
      this.remove(type, id);
    }

    return toRemove.length;
  }

  onAdd<T extends Entity>(type: EntityType, handler: EntityLifecycleHandler<T>): () => void {
    if (!this.addHandlers.has(type)) {
      this.addHandlers.set(type, new Set());
    }
    this.addHandlers.get(type)!.add(handler as EntityLifecycleHandler);

    return () => {
      this.addHandlers.get(type)?.delete(handler as EntityLifecycleHandler);
    };
  }

  onRemove<T extends Entity>(type: EntityType, handler: EntityLifecycleHandler<T>): () => void {
    if (!this.removeHandlers.has(type)) {
      this.removeHandlers.set(type, new Set());
    }
    this.removeHandlers.get(type)!.add(handler as EntityLifecycleHandler);

    return () => {
      this.removeHandlers.get(type)?.delete(handler as EntityLifecycleHandler);
    };
  }

  getTypes(): EntityType[] {
    return Array.from(this.entities.keys());
  }

  forEach<T extends Entity>(type: EntityType, callback: (entity: T) => void): void {
    const typeMap = this.entities.get(type);
    if (!typeMap) return;
    for (const entity of typeMap.values()) {
      callback(entity as T);
    }
  }

  forEachActive<T extends Entity>(type: EntityType, callback: (entity: T) => void): void {
    const typeMap = this.entities.get(type);
    if (!typeMap) return;
    for (const entity of typeMap.values()) {
      if (entity.active) {
        callback(entity as T);
      }
    }
  }
}
